import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import LeadDefaultsStep, {
    NO_DEFAULT_ASSIGNEE_VALUE,
    NO_DEFAULT_STATUS_VALUE,
} from '@/components/leads/mapping/LeadDefaultsStep';
import { useUpdateCampaign } from '@/api/campaigns/use-campaigns';
import type { Campaign } from '@/api/campaigns/types';

export interface DefaultsStepProps {
    campaign: Campaign;
    onBack: () => void;
    onAdvance: () => void;
}

const DefaultsStep: React.FC<DefaultsStepProps> = ({ campaign, onBack, onAdvance }) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const updateCampaign = useUpdateCampaign();

    const [leadStatusId, setLeadStatusId] = useState(
        campaign.default_lead_status_id ?? NO_DEFAULT_STATUS_VALUE
    );
    const [assigneeId, setAssigneeId] = useState(
        campaign.default_assignee_id ?? NO_DEFAULT_ASSIGNEE_VALUE
    );
    const [allowedOrigins, setAllowedOrigins] = useState<string[]>(campaign.allowed_origins ?? []);

    const isLandingPage = campaign.source === 'landing_page';

    const updateOrigin = (index: number, value: string) => {
        setAllowedOrigins(prev => prev.map((origin, i) => (i === index ? value : origin)));
    };

    const removeOrigin = (index: number) => {
        setAllowedOrigins(prev => prev.filter((_, i) => i !== index));
    };

    const addOrigin = () => {
        setAllowedOrigins(prev => [...prev, '']);
    };

    const handleContinue = async () => {
        try {
            await updateCampaign.mutateAsync({
                id: campaign.id,
                data: {
                    default_lead_status_id:
                        leadStatusId === NO_DEFAULT_STATUS_VALUE ? null : leadStatusId,
                    default_assignee_id:
                        assigneeId === NO_DEFAULT_ASSIGNEE_VALUE ? null : assigneeId,
                    ...(isLandingPage
                        ? {
                              allowed_origins: allowedOrigins
                                  .map(origin => origin.trim())
                                  .filter(origin => origin.length > 0),
                          }
                        : {}),
                },
            });
            enqueueSnackbar(t('campaigns.editor.defaults.saved'), { variant: 'success' });
            onAdvance();
        } catch {
            enqueueSnackbar(t('campaigns.editor.defaults.saveFailed'), { variant: 'error' });
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{t('campaigns.editor.defaults.title')}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        {t('campaigns.editor.defaults.description')}
                    </p>
                </CardHeader>
                <CardContent className="space-y-6">
                    <LeadDefaultsStep
                        value={{ leadStatusId, assigneeId }}
                        onChange={value => {
                            setLeadStatusId(value.leadStatusId);
                            setAssigneeId(value.assigneeId);
                        }}
                    />
                </CardContent>
            </Card>

            {isLandingPage && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('campaigns.editor.defaults.allowedOriginsTitle')}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.editor.defaults.allowedOriginsDescription')}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {allowedOrigins.map((origin, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <Input
                                    value={origin}
                                    onChange={e => updateOrigin(index, e.target.value)}
                                    placeholder="https://example.com"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => removeOrigin(index)}
                                    aria-label={t('actions.delete')}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addOrigin}>
                            <Plus className="h-4 w-4 mr-1" />
                            {t('campaigns.editor.defaults.addOrigin')}
                        </Button>
                        {allowedOrigins.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                {t('campaigns.editor.defaults.noOriginsHint')}
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between border-t pt-4">
                <Button variant="outline" onClick={onBack}>
                    {t('campaigns.editor.actions.back')}
                </Button>
                <Button onClick={handleContinue} disabled={updateCampaign.isPending}>
                    {updateCampaign.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {t('campaigns.editor.actions.saveAndContinue')}
                </Button>
            </div>
        </div>
    );
};

export default DefaultsStep;
